import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Button from '../components/Button.jsx';
import LazyImage from '../components/LazyImage.jsx';
import { TextAnimate } from '../components/ui/text-animate.jsx';
import { BlurFade } from '../components/ui/blur-fade.jsx';
import '../styles/Home.css';
import weddingClipart from '../assets/images/Clipart Wedding Invitations.webp';
import birthdayClipart from '../assets/images/Clipart Birthday Invitations.webp';
import christeningClipart from '../assets/images/Clipart Christening Invitations.webp';
import corporateClipart from '../assets/images/Clipart Corporate Events Invitations.webp';
import { 
  IoSparkles, 
  IoStar, 
  IoArrowForward,
  IoHeart,
  IoGift,
  IoCalendar,
  IoChevronBack,
  IoChevronForward
} from 'react-icons/io5';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || '';

const Home = () => {
  const [publicReviews, setPublicReviews] = useState([]);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [expandedReview, setExpandedReview] = useState(null);
  const sliderRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const dragState = useRef({ isDown: false, startX: 0, scrollLeft: 0 });
  const [isDragging, setIsDragging] = useState(false);

  const services = [
    {
      title: 'Wedding Invitations',
      description: 'Elegant and romantic digital invitations for your special day',
      icon: weddingClipart,
      iconImage: true,
      link: '/wedding'
    },
    {
      title: 'Birthday Invitations',
      description: 'Fun and colorful designs for all ages and themes',
      icon: birthdayClipart,
      iconImage: true,
      link: '/birthday'
    },
    {
      title: 'Christening & Baptism',
      description: 'Beautiful and meaningful invitations for religious celebrations',
      icon: christeningClipart,
      iconImage: true,
      link: '/christening'
    },
    {
      title: 'Corporate Events',
      description: 'Professional designs for seminars, launches, and company events',
      icon: corporateClipart,
      iconImage: true,
      link: '/corporate'
    }
  ];

  const templateFeatures = [
    '✓ Choose from 12 ready-made designs',
    '✓ Personalized with your details',
    '✓ Interactive RSVP form included',
    '✓ Real-time RSVP tracking & summary',
    '✓ Shareable link for easy distribution',
    '✓ Mobile-friendly responsive design',
    '✓ Fast delivery (24-48 hours)'
  ];

  const customFeatures = [
    '✓ Fully custom design from scratch',
    '✓ Up to 10 revisions',
    '✓ Unique design tailored to your theme',
    '✓ Premium animations & effects',
    '✓ Real-time RSVP tracking & summary',
    '✓ Priority support & consultation',
    '✓ Fast delivery (24hrs - 3 days)'
  ];

  const testimonials = [
    {
      name: 'Maria Santos',
      event: 'Wedding Invitation',
      rating: 5,
      message: 'Sobrang ganda ng design! Lahat ng guests namin namangha sa e-invitation namin. Very professional and responsive ang Logica team. Highly recommended!',
      avatar: 'MS',
      date: '2026-03-20'
    },
    {
      name: 'John & Anna Cruz',
      event: 'Wedding Invitation',
      rating: 5,
      message: 'Best decision to go digital! The design was exactly what we wanted. Mabilis at maganda ang output. Thank you Logica Technology!',
      avatar: 'JC',
      date: '2026-03-28'
    },
    {
      name: 'Kristine Reyes',
      event: 'Birthday Invitation',
      rating: 5,
      message: 'Napakaganda ng birthday invitation ng anak ko! Ang creative at unique ng design. Will definitely order again for future events!',
      avatar: 'KR',
      date: '2026-04-02'
    },
    {
      name: 'Roberto Mendoza',
      event: 'Christening Invitation',
      rating: 5,
      message: 'Very accommodating at patient sa revisions. The final output exceeded our expectations. Affordable price for such quality work!',
      avatar: 'RM',
      date: '2026-04-05'
    },
    {
      name: 'Lovely & Mark Garcia',
      event: 'Wedding Invitation',
      rating: 5,
      message: 'Super satisfied kami! From design to final output, everything was smooth. The team really listened to what we wanted. 100% worth it!',
      avatar: 'LG',
      date: '2026-04-08'
    },
    {
      name: 'Angela Torres',
      event: 'Corporate Event',
      rating: 5,
      message: 'Professional service! Our company seminar invitation looked very elegant. Fast delivery and great communication throughout the process.',
      avatar: 'AT',
      date: '2026-04-12'
    }
  ];

  const getRelativeTime = (dateStr) => {
    const now = new Date();
    const past = new Date(dateStr);
    const diffDays = Math.floor((now - past) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return '1 day ago';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 14) return '1 week ago';
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 60) return '1 month ago';
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  const getInitials = (name) => String(name || 'Guest')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || 'G';

  const displayedTestimonials = publicReviews.length > 0
    ? publicReviews.slice(0, 10).map((review) => ({
      name: review.reviewer_name || 'Customer',
      event: review.template_name || (review.event_type ? review.event_type.charAt(0).toUpperCase() + review.event_type.slice(1) : null) || 'E-Invitation',
      rating: Number(review.rating) || 5,
      ratingDesign: review.rating_design ? Number(review.rating_design) : null,
      ratingCommunication: review.rating_communication ? Number(review.rating_communication) : null,
      ratingValue: review.rating_value ? Number(review.rating_value) : null,
      message: review.comment || '',
      avatar: getInitials(review.reviewer_name),
      date: review.submitted_at || review.created_at,
      adminReply: review.is_published ? review.admin_reply : null,
      images: (() => { try { return review.review_images ? JSON.parse(review.review_images) : []; } catch { return []; } })()
    }))
    : testimonials.slice(0, 10);

  const hiwSteps = [
    { number: 1, icon: '📋', title: 'Book Online', desc: 'Fill out the booking form with your event details and preferred design.' },
    { number: 2, icon: '💬', title: 'We Reach Out', desc: 'Our team contacts you via Messenger within 24 hours to confirm your order.' },
    { number: 3, icon: '💳', title: '50% Downpayment', desc: 'We send payment details through Messenger. Pay 50% to get started.' },
    { number: 4, icon: '🎨', title: 'Design Creation', desc: 'We craft your personalized e-invitation based on your preferences.' },
    { number: 5, icon: '✅', title: 'Review & Approve', desc: 'Check the design. Request revisions if needed, then give your approval.' },
    { number: 6, icon: '💰', title: 'Full Payment', desc: 'Once approved, settle the remaining 50% balance to receive your final file.' },
    { number: 7, icon: '⭐', title: 'Share & Feedback', desc: 'Share your invitation with guests and leave us feedback via Messenger or tag us in your post!' },
  ];

  const hiwCardsRef = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('hiw-card--visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    hiwCardsRef.current.forEach(el => { if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function fetchPublishedReviews() {
      try {
        const res = await fetch(`${API_BASE_URL}/reviews/published`);
        const json = await res.json().catch(() => ({}));
        if (!cancelled && res.ok && Array.isArray(json.reviews)) {
          setPublicReviews(json.reviews);
        }
      } catch (_) {
        if (!cancelled) setPublicReviews([]);
      }
    }

    fetchPublishedReviews();

    // Refetch whenever the tab becomes visible (e.g. after switching from admin panel)
    function onVisible() {
      if (document.visibilityState === 'visible') fetchPublishedReviews();
    }
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  // Carousel arrow sync
  const updateArrows = () => {
    const el = sliderRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  };

  const scrollSlider = (dir) => {
    const el = sliderRef.current;
    if (!el) return;
    // scroll by the visible track width → exactly 1 "page" on mobile, batch on desktop
    el.scrollBy({ left: dir === 'right' ? el.clientWidth : -el.clientWidth, behavior: 'smooth' });
  };

  const handleMouseDown = (e) => {
    const el = sliderRef.current;
    if (!el) return;
    dragState.current = { isDown: true, startX: e.pageX - el.offsetLeft, scrollLeft: el.scrollLeft };
    setIsDragging(true);
  };

  const handleMouseMove = (e) => {
    if (!dragState.current.isDown) return;
    e.preventDefault();
    const el = sliderRef.current;
    if (!el) return;
    const x = e.pageX - el.offsetLeft;
    const walk = (x - dragState.current.startX) * 1.4;
    el.scrollLeft = dragState.current.scrollLeft - walk;
  };

  const stopDrag = () => {
    dragState.current.isDown = false;
    setIsDragging(false);
  };

  useEffect(() => {
    const el = sliderRef.current;
    if (!el) return;
    updateArrows();
    el.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows);
    return () => {
      el.removeEventListener('scroll', updateArrows);
      window.removeEventListener('resize', updateArrows);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayedTestimonials]);

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-background-effects">
          <div className="gradient-blob blob-1"></div>
          <div className="gradient-blob blob-2"></div>
          <div className="gradient-blob blob-3"></div>
        </div>
        
        {/* Left Side - Content */}
        <div className="hero-content">
          <BlurFade delay={0.2} direction="up">
            <h1 className="hero-title">
              <span className="gradient-text">Stunning Digital</span>
              <span className="hero-subtitle">Invitations for Every Occasion</span>
            </h1>
          </BlurFade>
          <BlurFade delay={0.6} direction="up">
            <p className="hero-description">
              Make your special moments unforgettable with beautifully crafted 
              e-invitations. Perfect for weddings, birthdays, christenings, and more!
            </p>
          </BlurFade>

          <BlurFade delay={0.8} direction="up">
            <div className="hero-actions">
              <Link to="/book" className="hero-cta-link">
                <Button variant="primary" size="large" className="hero-cta-primary">
                  <span>Book Now</span>
                  <IoArrowForward className="btn-icon" />
                </Button>
              </Link>
            </div>
          </BlurFade>
        </div>

        {/* Right Side - Visual */}
        <BlurFade delay={0.4} direction="right" className="hero-visual">
          <div className="hero-image-container">
            <div className="hero-image-circle">
              <LazyImage 
                src={weddingClipart} 
                alt="Beautiful E-Invitation Design"
                effect="blur"
                threshold={300}
                priority={true}
              />
            </div>
          </div>
        </BlurFade>
      </section>

      {/* Services Section */}
      <section className="services">
        <div className="section-header">
          <h2>E-Invitation Services</h2>
          <p>Browse our collection of digital invitation designs</p>
        </div>
        <div className="services-grid">
          {services.map((service, index) => (
            <Link to={service.link} key={index} className="service-card">
              <div className="service-card-inner">
                {service.iconImage ? (
                  <div className="service-icon-wrapper">
                    <LazyImage 
                      src={service.icon} 
                      alt={service.title} 
                      className="service-icon-img"
                      effect="blur"
                      threshold={200}
                    />
                  </div>
                ) : (
                  <div className="service-icon">{service.icon}</div>
                )}
                <h3>{service.title}</h3>
                <p>{service.description}</p>
                <span className="view-samples">
                  <span>View Samples</span>
                  <IoArrowForward className="arrow-icon" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works">
        <div className="section-header">
          <h2>How It Works</h2>
          <p>Simple steps from booking to your beautiful e-invitation</p>
        </div>
        <div className="hiw-timeline">
          {hiwSteps.map((step, index) => (
            <div
              key={index}
              className="hiw-card"
              ref={el => { hiwCardsRef.current[index] = el; }}
              style={{ '--hiw-delay': `${index * 0.09}s` }}
            >
              <div className="hiw-track">
                <div className="hiw-dot">{step.number}</div>
                {index < hiwSteps.length - 1 && <div className="hiw-connector" />}
              </div>
              <div className="hiw-card-body">
                <div className="hiw-icon-wrap">{step.icon}</div>
                <div className="hiw-text">
                  <h4>{step.title}</h4>
                  <p>{step.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="pricing">
        <div id="pricing-heading" className="section-header">
          <h2>Affordable Pricing</h2>
          <p>Quality designs that fit your budget</p>
        </div>
        <div className="pricing-grid">
          {/* Template Pricing Card */}
          <div className="pricing-card">
            <div className="pricing-header">
              <span className="pricing-type">Template Designs</span>
              <div className="pricing-amount">
                <span className="currency">₱</span>
                <span className="price">200</span>
              </div>
              <span className="pricing-note">Choose from our ready-made templates</span>
            </div>
            <div className="pricing-features">
              <h4>What's Included:</h4>
              <ul>
                {templateFeatures.map((feature, index) => (
                  <li key={index}>
                    <IoStar className="feature-icon" />
                    <span>{feature.replace('✓ ', '')}</span>
                  </li>
                ))}
              </ul>
            </div>
            <Link to="/invitations">
              <Button variant="outline-primary" size="large" className="pricing-btn">
                Browse Templates
              </Button>
            </Link>
          </div>

          {/* Custom Design Pricing Card */}
          <div className="pricing-card featured">
            <div className="pricing-badge">
              <IoSparkles className="badge-icon-small" />
              <span>Most Popular</span>
            </div>
            <div className="pricing-header">
              <span className="pricing-type">Custom Designs</span>
              <div className="pricing-amount">
                <span className="currency">₱</span>
                <span className="price">350</span>
                <span className="price-range-sep">–</span>
                <span className="currency">₱</span>
                <span className="price">700</span>
              </div>
              <span className="pricing-note">Unique design made just for you</span>
            </div>
            <div className="pricing-features">
              <h4>What's Included:</h4>
              <ul>
                {customFeatures.map((feature, index) => (
                  <li key={index}>
                    <IoStar className="feature-icon" />
                    <span>{feature.replace('✓ ', '')}</span>
                  </li>
                ))}
              </ul>
            </div>
            <Link to="/book">
              <Button variant="primary" size="large" className="pricing-btn">
                Book Custom Design
              </Button>
            </Link>
          </div>
        </div>

        <div className="payment-terms-note">
          <span className="payment-terms-icon">💳</span>
          <div>
            <strong>Payment Terms:</strong> 50% downpayment upon booking. Remaining 50% is due once the design is completed and approved by the client.
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials">
        <div className="testimonials-header">
          <div className="testimonials-header-left">
            <h2>What Our Clients Say</h2>
            <p>Real reviews from happy customers. Trusted for quality, creativity, and seamless service every time.</p>
          </div>
          <div className="testimonials-header-right">
            <div className="testimonials-arrow-group">
              <button
                type="button"
                className="testimonials-arrow testimonials-arrow--left"
                onClick={() => scrollSlider('left')}
                disabled={!canScrollLeft}
                aria-label="Scroll left"
              >
                <IoChevronBack />
              </button>
              <button
                type="button"
                className="testimonials-arrow testimonials-arrow--right"
                onClick={() => scrollSlider('right')}
                disabled={!canScrollRight}
                aria-label="Scroll right"
              >
                <IoChevronForward />
              </button>
            </div>
          </div>
        </div>
        <div className="testimonials-container">
          <div
            className={`testimonials-track${isDragging ? ' is-dragging' : ''}`}
            ref={sliderRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={stopDrag}
            onMouseLeave={stopDrag}
          >
            {displayedTestimonials.map((testimonial, index) => {
              const isExpanded = expandedReview === index;
              const hasLongMsg = testimonial.message && testimonial.message.length > 100;
              return (
                <div key={index} className="testimonial-card">
                  {/* Top row: avatar + name/time + rating */}
                  <div className="tc-top">
                    <div className="testimonial-avatar">{testimonial.avatar}</div>
                    <div className="tc-meta">
                      <span className="tc-name">{testimonial.name}</span>
                      <span className="tc-time">{getRelativeTime(testimonial.date)}</span>
                    </div>
                    <div className="tc-rating">
                      {Array.from({ length: testimonial.rating }).map((_, i) => (
                        <IoStar key={i} className="star-icon" />
                      ))}
                      <span className="testimonial-rating-num">{testimonial.rating.toFixed(1)}</span>
                    </div>
                  </div>

                  {/* Event badge */}
                  {testimonial.event && (
                    <span className="testimonial-event-badge">{testimonial.event}</span>
                  )}

                  {/* Comment — truncated unless expanded */}
                  {testimonial.message && (
                    <p className={`tc-comment${isExpanded ? ' tc-comment--expanded' : ''}`}>
                      {testimonial.message}
                    </p>
                  )}
                  {hasLongMsg && (
                    <button type="button" className="tc-expand-btn" onClick={() => setExpandedReview(isExpanded ? null : index)}>
                      {isExpanded ? 'Show less ▲' : 'Read more ▼'}
                    </button>
                  )}

                  {/* Aspect ratings — compact inline pills */}
                  {(testimonial.ratingDesign || testimonial.ratingCommunication || testimonial.ratingValue) && (
                    <div className="tc-aspects">
                      {testimonial.ratingDesign && <span className="tc-aspect-pill">Design {'★'.repeat(testimonial.ratingDesign)}</span>}
                      {testimonial.ratingCommunication && <span className="tc-aspect-pill">Comms {'★'.repeat(testimonial.ratingCommunication)}</span>}
                      {testimonial.ratingValue && <span className="tc-aspect-pill">Value {'★'.repeat(testimonial.ratingValue)}</span>}
                    </div>
                  )}

                  {/* Review images */}
                  {testimonial.images && testimonial.images.length > 0 && (
                    <div className="testimonial-images">
                      {testimonial.images.map((src, i) => (
                        <button key={i} type="button" className="testimonial-image" onClick={() => setLightboxSrc(`${import.meta.env.VITE_API_BASE_URL}${src}`)} aria-label={`View review photo ${i+1}`}>
                          <img src={`${import.meta.env.VITE_API_BASE_URL}${src}`} alt={`Review photo ${i+1}`} loading="lazy" />
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Admin reply */}
                  {testimonial.adminReply && (
                    <div className="testimonial-admin-reply">
                      <span>Response from E-Invitation</span>
                      <p>{testimonial.adminReply}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta">
        <div className="cta-background-pattern"></div>
        <div className="cta-container">
          <div className="cta-content-left">
        
            <h2>Ready to Create Your Digital Invitation?</h2>
            <p>Take the next step toward transforming your special events. With our beautifully designed e-invitations, share your joy with guests in style and stand out from traditional paper cards.</p>
            <div className="cta-actions">
              <Link to="/book">
                <Button variant="primary" size="large" className="cta-btn-primary">
                  <span>Get started now</span>
                  <IoArrowForward className="btn-icon" />
                </Button>
              </Link>
              <Link to="/invitations">
                <Button variant="outline" size="large" className="cta-btn-secondary">
                  Try demo for free
                </Button>
              </Link>
            </div>
          </div>
          <div className="cta-content-right">
            <div className="cta-phone-mockup">
              <div className="phone-frame">
                <div className="phone-notch"></div>
                <div className="phone-screen">
                  <div className="phone-status-bar">
                    <span className="status-time">1:47</span>
                    <div className="status-icons">
                      <div className="status-signal"></div>
                      <div className="status-wifi"></div>
                      <div className="status-battery"></div>
                    </div>
                  </div>
                  <div className="phone-content">
                    <div className="invitation-preview">
                      <div className="invitation-header">
                        <IoHeart className="invitation-icon" />
                        <h3>Wedding Invitation</h3>
                      </div>
                      <div className="invitation-card">
                        <LazyImage 
                          src={weddingClipart} 
                          alt="Invitation Preview" 
                          className="invitation-image"
                          effect="blur"
                          threshold={200}
                        />
                        <div className="invitation-details">
                          <h4>John & Sarah</h4>
                          <p>You're invited to our special day!</p>
                          <div className="invitation-date">
                            <IoCalendar className="date-icon" />
                            <span>June 15, 2026</span>
                          </div>
                        </div>
                      </div>
                      <button className="rsvp-button">
                        <span>RSVP Now</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Image lightbox */}
      {lightboxSrc && (
        <div
          className="testimonial-lightbox-backdrop"
          onClick={() => setLightboxSrc(null)}
          onKeyDown={e => e.key === 'Escape' && setLightboxSrc(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Image preview"
          tabIndex={-1}
        >
          <button
            className="testimonial-lightbox-close"
            onClick={() => setLightboxSrc(null)}
            aria-label="Close image"
          >✕</button>
          <img
            src={lightboxSrc}
            alt="Review photo"
            className="testimonial-lightbox-img"
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default Home;
